import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { EMAIL_DASHBOARD_VERIFICATION_KEY } from "@/constants/auth";

interface EmailAdminRouteGuardProps {
  children: ReactNode;
}

export function EmailAdminRouteGuard({ children }: EmailAdminRouteGuardProps) {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Check if the user is verified through the special verification
        const verified = localStorage.getItem(EMAIL_DASHBOARD_VERIFICATION_KEY);
        
        if (verified === "true") {
          setIsAuthorized(true);
          return;
        }
        
        // If not verified through special verification, check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setIsAuthorized(false);
          return;
        }
        
        // Check if user has admin role
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        // Only allow admin or super_admin roles
        const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
        setIsAuthorized(isAdmin);
      } catch (error) {
        console.error('Error checking email dashboard access:', error);
        setIsAuthorized(false);
      }
    };

    checkAccess();
  }, []);

  // If we're still checking, show loading spinner
  if (isAuthorized === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If not authorized, redirect to the verification page
  if (!isAuthorized) {
    return <Navigate to="/admin/email/verify" state={{ from: location }} replace />;
  }

  // If authorized, render the children
  return <>{children}</>;
}
